// frontend/src/pages/doctor/Schedule.js
import React, { useState, useEffect } from 'react';
import { Clock, Save, Plus, Trash2 } from 'lucide-react';
import doctorService from '../../services/doctorService';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const Schedule = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availability, setAvailability] = useState({
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: []
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const days = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await doctorService.getProfile();
      
      if (response.success && response.data.availability) {
        setAvailability(response.data.availability);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch schedule');
    } finally {
      setLoading(false);
    }
  };

  const addTimeSlot = (day) => {
    setAvailability(prev => ({
      ...prev,
      [day]: [...prev[day], { start: '09:00', end: '17:00' }]
    }));
  };

  const removeTimeSlot = (day, index) => {
    setAvailability(prev => ({
      ...prev,
      [day]: prev[day].filter((_, i) => i !== index)
    }));
  };

  const updateTimeSlot = (day, index, field, value) => {
    setAvailability(prev => ({
      ...prev,
      [day]: prev[day].map((slot, i) =>
        i === index ? { ...slot, [field]: value } : slot
      )
    }));
  };

  const copyToAllDays = (sourceDay) => {
    const slots = availability[sourceDay];
    const newAvailability = {};
    
    days.forEach(day => {
      newAvailability[day.key] = JSON.parse(JSON.stringify(slots));
    });
    
    setAvailability(newAvailability);
    setSuccess(`Copied ${sourceDay}'s schedule to all days`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const response = await doctorService.updateAvailability(availability);
      
      if (response.success) {
        setSuccess('Schedule updated successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.message || 'Failed to update schedule');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Manage Schedule</h1>
          <p className="text-gray-600 mt-1">Set your availability for consultations</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Schedule'}
        </button>
      </div>

      {/* Success Message */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-lg">
          {success}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Schedule Grid */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="space-y-6">
          {days.map(({ key, label }) => (
            <div key={key} className="border-b border-gray-200 pb-6 last:border-0">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{label}</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => copyToAllDays(key)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Copy to all days
                  </button>
                  <button
                    onClick={() => addTimeSlot(key)}
                    className="flex items-center text-sm text-primary-600 hover:text-primary-800"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Slot
                  </button>
                </div>
              </div>

              {availability[key].length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <Clock className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-gray-500 text-sm">No time slots added</p>
                  <button
                    onClick={() => addTimeSlot(key)}
                    className="mt-2 text-sm text-primary-600 hover:text-primary-800"
                  >
                    Add your first time slot
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {availability[key].map((slot, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      <div className="flex-1 grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Start Time
                          </label>
                          <input
                            type="time"
                            value={slot.start}
                            onChange={(e) => updateTimeSlot(key, index, 'start', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            End Time
                          </label>
                          <input
                            type="time"
                            value={slot.end}
                            onChange={(e) => updateTimeSlot(key, index, 'end', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => removeTimeSlot(key, index)}
                        className="mt-6 text-red-600 hover:text-red-800"
                        title="Remove slot"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">Scheduling Tips</h4>
        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
          <li>Set realistic time slots that you can consistently maintain</li>
          <li>Leave buffer time between consultations for notes and breaks</li>
          <li>Consider different time zones if you serve international patients</li>
          <li>Remember to save your changes after updating your schedule</li>
        </ul>
      </div>
    </div>
  );
};

export default Schedule;